export const isRelativePath = (url: string): boolean =>
    url.startsWith('/') && !url.startsWith('//');

/**
 * Validates a redirect path string: relative URLs and absolute URL path segments
 * (pathname + search + hash). Rejects protocol-relative URLs and backslash bypass
 * payloads. Whether `/` is an appropriate destination is left to the caller.
 */
export const isValidPath = (path: string): boolean => {
    if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) {
        return false;
    }

    try {
        const decodedPath = decodeURIComponent(path);

        if (decodedPath.includes('\\')) {
            return false;
        }
    } catch {
        return false;
    }

    return true;
};

export const isSameDomainHost = (url: string): boolean => {
    if (typeof window === 'undefined' || !window.location.hostname) {
        return false;
    }

    try {
        const parsed = new URL(url);
        return parsed.hostname.toLowerCase() === window.location.hostname.toLowerCase();
    } catch {
        return false;
    }
};

const getPathFromAbsoluteUrl = (url: string): string | null => {
    try {
        const parsed = new URL(url);
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
        return null;
    }
};

/**
 * Validates that a client-side redirect target is safe to navigate to.
 *
 * Relative targets are validated by path alone. Absolute targets must match the
 * current hostname; when they do, the hostname is dropped and the returned value
 * is pathname + search + hash.
 *
 * @param url - Redirect or referer URL to validate.
 * @returns A safe relative redirect target (same-domain absolute URLs are normalized to
 *   pathname + search + hash), otherwise `null`.
 */
export const getValidRedirectUrl = (url: string | null | undefined): string | null => {
    if (!url) {
        return null;
    }

    if (isRelativePath(url)) {
        return isValidPath(url) ? url : null;
    }

    if (isSameDomainHost(url)) {
        const path = getPathFromAbsoluteUrl(url);
        return path && isValidPath(path) ? path : null;
    }

    return null;
};
